// NOTE : UserDoc corresponds to User entity

import { DocumentData, QueryDocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import dayjs, { Dayjs } from 'dayjs';
import { EPhoneDisplayType, UserRole } from '@leedpulse-monorepo/enums';
import UserEmails from './user_doc/UserEmails';
import { TUserCompanies, TUserSubscriptions } from '@leedpulse-monorepo/types';
import { IAvailabilityHours, IConnector, IUserFile, IAppLeedflow } from '@leedpulse-monorepo/interfaces';
import UserHistory from './user_doc/UserHistory';

// But because it clashes with the User Firebase type, it is named UserDoc
export default class UserDoc {
  private _id: string;
  private _uid: string;
  private _avatar: string;
  private _email: string; // send(create)
  private _emailAuthentication: string; // send(create)
  private _emails: UserEmails;
  private _firstname: string; // send(create)
  private _idCompanies: TUserCompanies;
  private _idSubscription: TUserSubscriptions;
  private _lastname: string; // send(create)
  private _phone: string; // send(create)
  private _role: UserRole; // send(create)
  private _daysOffStart: Dayjs | null;
  private _daysOffEnd: Dayjs | null;
  private _daysOffMessage: string;
  private _created: number;
  private _updated: number;
  private _last_login: number;
  private _partnerUid: string;
  private _partnerSystem: string;
  private _position: string;
  private _voicemailTxtToSpeech: string;
  private _availabilityHours: IAvailabilityHours | null;
  private _history: UserHistory[];
  private _files: IUserFile[];
  private _legalEntity: boolean;
  private _voip_enabled: boolean;
  private _recordingAnnouncementEnabled: boolean;
  private _calendarTasksCreationEnabled: boolean;
  private _smsDisabled: boolean;
  private _doNotDisturb: boolean;
  private _phoneDisplayType: EPhoneDisplayType;
  private _connectors: IConnector[];
  private _notes: string;
  private _appLeedflow: IAppLeedflow | null;

  constructor(
    id: string,
    uid: string,
    avatar: string,
    email: string,
    emailAuthentication: string,
    emails: UserEmails,
    firstname: string,
    idCompanies: TUserCompanies,
    idSubscription: TUserSubscriptions,
    lastname: string,
    phone: string,
    role: UserRole,
    daysOffStart: Dayjs | null = null,
    daysOffEnd: Dayjs | null = null,
    daysOffMessage = '',
    created: number,
    updated: number,
    last_login: number,
    partner_uid: string,
    partner_system: string,
    position: string,
    voicemailTxtToSpeech: string,
    availabilityHours: IAvailabilityHours | null = null,
    history: UserHistory[] = [],
    files: IUserFile[] = [],
    legalEntity = false,
    voip_enabled = false,
    recordingAnnouncementEnabled = true,
    calendarTasksCreationEnabled = true,
    smsDisabled = false,
    doNotDisturb = false,
    phoneDisplayType: EPhoneDisplayType = EPhoneDisplayType.proxy,
    connectors: IConnector[] = [],
    notes = '',
    appLeedflow: IAppLeedflow | null = null
  ) {
    this._id = id;
    this._uid = uid;
    this._avatar = avatar;
    this._email = email;
    this._emailAuthentication = emailAuthentication;
    this._emails = emails;
    this._firstname = firstname;
    this._idCompanies = idCompanies;
    this._idSubscription = idSubscription;
    this._lastname = lastname;
    this._phone = phone;
    this._role = role;
    this._daysOffStart = daysOffStart;
    this._daysOffEnd = daysOffEnd;
    this._daysOffMessage = daysOffMessage;
    this._created = created;
    this._updated = updated;
    this._last_login = last_login;
    this._partnerUid = partner_uid;
    this._partnerSystem = partner_system;
    this._position = position;
    this._voicemailTxtToSpeech = voicemailTxtToSpeech;
    this._availabilityHours = availabilityHours;
    this._history = history;
    this._files = files;
    this._legalEntity = legalEntity;
    this._voip_enabled = voip_enabled;
    this._recordingAnnouncementEnabled = recordingAnnouncementEnabled;
    this._calendarTasksCreationEnabled = calendarTasksCreationEnabled;
    this._smsDisabled = smsDisabled;
    this._doNotDisturb = doNotDisturb;
    this._phoneDisplayType = phoneDisplayType;
    this._connectors = connectors;
    this._notes = notes;
    this._appLeedflow = appLeedflow;
  }

  get id(): string {
    return this._id;
  }

  get uid(): string {
    return this._uid;
  }

  get avatar(): string {
    return this._avatar;
  }

  get email(): string {
    return this._email;
  }

  get emailAuthentication(): string {
    return this._emailAuthentication;
  }

  get emails(): UserEmails {
    return this._emails;
  }

  get firstname(): string {
    return this._firstname;
  }

  get idCompanies(): TUserCompanies {
    return this._idCompanies;
  }

  get idSubscription(): TUserSubscriptions {
    return this._idSubscription;
  }

  get lastname(): string {
    return this._lastname;
  }

  get phone(): string {
    return this._phone;
  }

  get role(): UserRole {
    return this._role;
  }

  get daysOffStart(): Dayjs | null {
    return this._daysOffStart;
  }

  get daysOffEnd(): Dayjs | null {
    return this._daysOffEnd;
  }

  get daysOffMessage(): string {
    return this._daysOffMessage;
  }

  get daysOffAsTimestamps(): [number | null, number | null] {
    return [
      this._daysOffStart?.valueOf() || null,
      this._daysOffEnd?.valueOf() || null,
    ];
  }

  get created(): number {
    return this._created;
  }

  get updated(): number {
    return this._updated;
  }

  get last_login(): number {
    return this._last_login;
  }

  get partnerUid(): string {
    return this._partnerUid;
  }

  get partnerSystem(): string {
    return this._partnerSystem;
  }

  get position(): string {
    return this._position;
  }

  get voicemailTxtToSpeech(): string {
    return this._voicemailTxtToSpeech;
  }

  get availabilityHours(): IAvailabilityHours | null {
    return this._availabilityHours;
  }

  get history(): UserHistory[] {
    return this._history;
  }

  get files(): IUserFile[] {
    return this._files;
  }

  get legalEntity(): boolean {
    return this._legalEntity;
  }

  get voip_enabled(): boolean {
    return this._voip_enabled;
  }

  get recordingAnnouncementEnabled(): boolean {
    return this._recordingAnnouncementEnabled;
  }

  get calendarTasksCreationEnabled(): boolean {
    return this._calendarTasksCreationEnabled;
  }

  get smsDisabled(): boolean {
    return this._smsDisabled;
  }

  get doNotDisturb(): boolean {
    return this._doNotDisturb;
  }

  get phoneDisplayType(): EPhoneDisplayType {
    return this._phoneDisplayType;
  }

  get connectors(): IConnector[] {
    return this._connectors;
  }

  get notes(): string {
    return this._notes;
  }

  get appLeedflow(): IAppLeedflow | null {
    return this._appLeedflow;
  }

  public copyWith({
    avatar,
    emails,
    firstname,
    lastname,
    idCompanies,
    idSubscription,
    phone,
    role,
    daysOffStart,
    daysOffEnd,
    daysOffMessage,
    created,
    updated,
    last_login,
    partner_uid,
    partner_system,
    position,
    voicemailTxtToSpeech,
    availabilityHours,
    history,
    files,
    legalEntity,
    voip_enabled,
    recordingAnnouncementEnabled,
    calendarTasksCreationEnabled,
    smsDisabled,
    doNotDisturb,
    phoneDisplayType,
    notes,
  }: {
    avatar?: string;
    emails?: UserEmails;
    firstname?: string;
    lastname?: string;
    idCompanies?: TUserCompanies;
    idSubscription?: TUserSubscriptions;
    phone?: string;
    role?: UserRole;
    daysOffStart?: Dayjs | null;
    daysOffEnd?: Dayjs | null;
    daysOffMessage?: string;
    created?: number;
    updated?: number;
    last_login?: number;
    partner_uid?: string;
    partner_system?: string;
    position?: string;
    voicemailTxtToSpeech?: string;
    availabilityHours?: IAvailabilityHours | null;
    history?: UserHistory[];
    files?: IUserFile[];
    legalEntity?: boolean;
    voip_enabled?: boolean;
    recordingAnnouncementEnabled?: boolean;
    calendarTasksCreationEnabled?: boolean;
    smsDisabled?: boolean;
    doNotDisturb?: boolean;
    phoneDisplayType?: EPhoneDisplayType;
    notes?: string;
  }): UserDoc {
    return new UserDoc(
      this._id,
      this._uid,
      avatar ?? this._avatar,
      this._email,
      this._emailAuthentication,
      emails ?? this._emails,
      firstname ?? this._firstname,
      idCompanies ?? this._idCompanies,
      idSubscription ?? this._idSubscription,
      lastname ?? this._lastname,
      phone ?? this._phone,
      role ?? this._role,
      daysOffStart ?? this._daysOffStart,
      daysOffEnd ?? this._daysOffEnd,
      daysOffMessage ?? this._daysOffMessage,
      created ?? this._created,
      updated ?? this._updated,
      last_login ?? this._last_login,
      partner_uid ?? this._partnerUid,
      partner_system ?? this._partnerSystem,
      position ?? this._position,
      voicemailTxtToSpeech ?? this._voicemailTxtToSpeech,
      availabilityHours !== undefined ? availabilityHours : this._availabilityHours,
      history ?? this._history,
      files ?? this._files,
      legalEntity ?? this._legalEntity,
      voip_enabled ?? this._voip_enabled,
      recordingAnnouncementEnabled ?? this._recordingAnnouncementEnabled,
      calendarTasksCreationEnabled ?? this._calendarTasksCreationEnabled,
      smsDisabled ?? this._smsDisabled,
      doNotDisturb ?? this._doNotDisturb,
      phoneDisplayType ?? this._phoneDisplayType,
      this._connectors,
      notes ?? this._notes,
      this._appLeedflow
    );
  }

  static fromFirestore(
    doc: QueryDocumentSnapshot<DocumentData, DocumentData>,
    history = null as QuerySnapshot<DocumentData, DocumentData> | null
  ): UserDoc {
    const docData = doc.data();
    return new UserDoc(
      docData.id,
      docData.uid,
      docData.avatar,
      docData.email,
      docData.email_authentication,
      UserEmails.fromFirestoreMap(docData.emails),
      docData.firstname,
      docData.id_companies,
      docData.id_subscription,
      docData.lastname,
      docData.phone,
      docData.role,
      docData.days_off?.[0] ? dayjs(docData.days_off[0]) : null,
      docData.days_off?.[1] ? dayjs(docData.days_off[1]) : null,
      docData.days_off_message || 'Je suis actuellement en congés et serai de retour prochainement. N\'hésitez pas à me rappeler à mon retour.',
      docData.created,
      docData.updated,
      docData.last_login,
      docData.partner_uid || '',
      docData.partner_system || '',
      docData.position || '',
      docData.voicemail_txt_to_speech || 'Je ne suis pas disponible, laissez-moi un message et je vous rappellerai rapidement.',
      docData.availability_hours ?? null,
      history ? history.docs.map((h) => UserHistory.fromFirestore(h)) : [],
      docData.files || [],
      docData.legal_entity || false,
      docData.voip_enabled ?? false,
      docData.recording_announcement_enabled ?? true,
      docData.calendar_tasks_creation_enabled ?? true,
      docData.sms_disabled ?? false,
      docData.do_not_disturb ?? false,
      docData.phone_display_type ?? EPhoneDisplayType.proxy,
      docData.connectors || [],
      docData.notes || '',
      docData.app_leedflow ?? null
    );
  }

  public toPatch(): Record<string, unknown> {
    return {
      id: this._id,
      uid: this._uid,
      avatar: this._avatar,
      emails: this._emails.toPatch(),
      firstname: this._firstname,
      lastname: this._lastname,
      id_companies: this._idCompanies,
      id_subscription: this._idSubscription,
      phone: this._phone,
      role: this._role,
      days_off: this.daysOffAsTimestamps,
      days_off_message: this._daysOffMessage,
      updated: new Date().getTime(),
      partner_uid: this._partnerUid,
      partner_system: this._partnerSystem,
      position: this._position,
      voicemail_txt_to_speech: this._voicemailTxtToSpeech,
      availability_hours: this._availabilityHours,
      history: this._history.map((history) => history.toFirestorePatch()),
      files: this._files,
      legal_entity: this._legalEntity,
      voip_enabled: this._voip_enabled,
      recording_announcement_enabled: this._recordingAnnouncementEnabled,
      calendar_tasks_creation_enabled: this._calendarTasksCreationEnabled,
      sms_disabled: this._smsDisabled,
      do_not_disturb: this._doNotDisturb,
      phone_display_type: this._phoneDisplayType,
      notes: this._notes,
    };
  }
}
